import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BreadcrumbService } from 'xng-breadcrumb';
import { FormService } from '../../../../../../shared/store/form/form.service';
import { SubHeaderService } from '../../../../../../shared/store/subheader/subheader.service';
import { View } from '../../../../../../shared/store/view/types';
import { ViewService } from '../../../../../../shared/store/view/views.service';
import { WorkScheduleService } from '../../../../../../shared/store/attendance/work-schedule/work-schedule.service';
import {
  DayOfWeek,
  WorkScheduleDayModel,
  WorkScheduleModel,
} from '../../../../../../shared/store/attendance/work-schedule/types';
import {
  WEEK_DAYS,
  dayAmplitude,
  dayOfWeekLabelKey,
  formatDuration,
} from '../work-schedule-helper';

/** Raw value of one row of the days FormArray (disabled times included). */
type WorkScheduleDayRow = {
  dayOfWeek: DayOfWeek;
  worked: boolean;
  startTime: string;
  endTime: string;
};

/**
 * End after start, on the endTime control itself: FormService.countErrors only counts leaf
 * controls, so a row-level error would block the submit without any toast.
 * "HH:mm" strings are zero-padded, so they compare directly.
 */
const endAfterStartValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const startTime = control.parent?.get('startTime')?.value;
  return startTime && control.value && control.value <= startTime
    ? { endBeforeStart: true }
    : null;
};

/** A plan without any worked day has no target at all. */
const atLeastOneWorkedDayValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null =>
  (control as FormArray).controls.some((x) => x.get('worked')?.value)
    ? null
    : { noWorkedDay: true };

/** "HH:mm" of an <input type="time"> → "HH:mm:ss" of the API (TimeOnly). */
const toApiTime = (time: string): string =>
  time.length === 5 ? `${time}:00` : time;

@UntilDestroy()
@Component({
  selector: 'app-work-schedule-form-content',
  standalone: false,
  templateUrl: './work-schedule-form-content.component.html',
  styleUrl: './work-schedule-form-content.component.scss',
})
export class WorkScheduleFormContentComponent implements OnInit, OnDestroy {
  view$: Observable<View>;
  workSchedule$: Observable<WorkScheduleModel>;
  workScheduleInstance$: Observable<WorkScheduleModel>;

  workScheduleForm: FormGroup;

  isEditMode = false;
  workScheduleId: number;

  /** Recomputed on every form change, so the preview input keeps a stable reference in between. */
  previewDays: WorkScheduleDayModel[] = [];
  rowAmplitudeLabels: string[] = [];

  protected readonly dayLabelKeys = WEEK_DAYS.map(dayOfWeekLabelKey);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly workScheduleService: WorkScheduleService,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formService: FormService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.workSchedule$ = this.workScheduleService.getById.value$;
    this.workScheduleInstance$ =
      this.workScheduleService.createInstance.value$;
  }

  get days(): FormArray {
    return this.workScheduleForm.controls['days'] as FormArray;
  }

  ngOnInit(): void {
    this.workScheduleForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      active: [true],
      days: this.formBuilder.array(
        WEEK_DAYS.map((dayOfWeek) => this.createDayGroup(dayOfWeek)),
        atLeastOneWorkedDayValidator,
      ),
    });

    this.workScheduleForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.refreshPreview());
    this.refreshPreview();

    this.workScheduleId = this.route.snapshot.params['id'];
    this.isEditMode = !!this.workScheduleId;

    if (this.isEditMode) {
      this.workScheduleService.getById.call(this.workScheduleId);
    } else {
      this.workScheduleService.createInstance.call();
    }

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'workScheduleCreate' || x == 'workScheduleUpdate')
          this.onSubmit();
        if (x == 'workScheduleList') {
          this.formService.clear();
          this.router.navigate(['/Configuration/WorkSchedules/List']);
        }
        if (x == 'workScheduleDetails') {
          this.formService.clear();
          this.router.navigate([
            '/Configuration/WorkSchedules/Details',
            this.workScheduleId,
          ]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
        if (!this.isEditMode) {
          this.breadcrumbService.set('@workScheduleCreate', x.breadcrumbCreate);
        }
      });

    this.workSchedule$
      .pipe(
        untilDestroyed(this),
        filter(() => this.isEditMode),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@workScheduleEdit', x.name);
        this.patchForm(x);
      });

    this.workScheduleInstance$
      .pipe(
        untilDestroyed(this),
        filter(() => !this.isEditMode),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x) => this.patchForm(x));
  }

  private createDayGroup(dayOfWeek: DayOfWeek): FormGroup {
    const group = this.formBuilder.group({
      dayOfWeek: [dayOfWeek],
      worked: [false],
      startTime: [{ value: '', disabled: true }, [Validators.required]],
      endTime: [
        { value: '', disabled: true },
        [Validators.required, endAfterStartValidator],
      ],
    });

    group.controls['worked'].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((worked) => this.onWorkedChange(group, !!worked));

    // endTime is validated against startTime: re-run it when the start moves.
    group.controls['startTime'].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() =>
        group.controls['endTime'].updateValueAndValidity({ emitEvent: false }),
      );

    return group;
  }

  /**
   * Unchecked → times cleared and disabled (no longer validated): a day not worked has no hours.
   * Checked on an empty row → prefilled with the hours of the previous worked day.
   */
  private onWorkedChange(group: FormGroup, worked: boolean) {
    const startTime = group.controls['startTime'];
    const endTime = group.controls['endTime'];

    if (!worked) {
      startTime.reset({ value: '', disabled: true }, { emitEvent: false });
      endTime.reset({ value: '', disabled: true }, { emitEvent: false });
      return;
    }

    if (!startTime.value && !endTime.value) {
      const source = this.findPreviousWorkedDay(group);
      if (source) {
        startTime.setValue(source.controls['startTime'].value, {
          emitEvent: false,
        });
        endTime.setValue(source.controls['endTime'].value, {
          emitEvent: false,
        });
      }
    }

    startTime.enable({ emitEvent: false });
    endTime.enable({ emitEvent: false });
  }

  /** Nearest worked day with hours before this one, wrapping around the week. */
  private findPreviousWorkedDay(group: FormGroup): FormGroup | null {
    const rows = this.days.controls as FormGroup[];
    const index = rows.indexOf(group);
    for (let offset = 1; offset < rows.length; offset++) {
      const candidate = rows[(index - offset + rows.length) % rows.length];
      if (
        candidate.controls['worked'].value &&
        candidate.controls['startTime'].value
      )
        return candidate;
    }
    return null;
  }

  private refreshPreview() {
    const rows: WorkScheduleDayRow[] = this.days.getRawValue();
    this.previewDays = this.toDayModels();
    this.rowAmplitudeLabels = rows.map((x) => {
      const minutes = x.worked ? dayAmplitude(x) : 0;
      return minutes > 0 ? formatDuration(minutes) : '-';
    });
  }

  /** Checked rows as API days: a day absent from the list is not worked. */
  private toDayModels(): WorkScheduleDayModel[] {
    const rows: WorkScheduleDayRow[] = this.days.getRawValue();
    return rows
      .filter((x) => x.worked)
      .map((x) => ({
        // The back rebuilds the days keyed by dayOfWeek and ignores both ids.
        id: 0,
        workScheduleId: this.isEditMode ? +this.workScheduleId : 0,
        dayOfWeek: x.dayOfWeek,
        startTime: toApiTime(x.startTime),
        endTime: toApiTime(x.endTime),
      }));
  }

  private patchForm(x: WorkScheduleModel) {
    this.workScheduleForm.patchValue({
      name: x.name,
      active: x.active,
      days: WEEK_DAYS.map((dayOfWeek) => {
        const day = x.days.find((d) => d.dayOfWeek === dayOfWeek);
        // Times before worked: checking the row must find them, not copy a neighbour's hours.
        return {
          startTime: day?.startTime.substring(0, 5) ?? '',
          endTime: day?.endTime.substring(0, 5) ?? '',
          worked: !!day,
        };
      }),
    });
  }

  onSubmit() {
    if (this.workScheduleForm.invalid) {
      this.workScheduleForm.markAllAsTouched();
      this.formService.setEntityErrors('WORK_SCHEDULE');
      this.formService.countErrors(this.workScheduleForm, true);
      return;
    }

    const workScheduleModel: WorkScheduleModel = {
      id: this.isEditMode ? +this.workScheduleId : 0,
      name: this.workScheduleForm.value.name,
      active: this.workScheduleForm.value.active,
      days: this.toDayModels(),
    };

    const result$ = this.isEditMode
      ? this.workScheduleService.update.call(workScheduleModel)
      : this.workScheduleService.add.call(workScheduleModel);

    result$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.formService.clear();
        this.router.navigate([
          '/Configuration/WorkSchedules/Details',
          result.id,
        ]);
      });
  }

  ngOnDestroy(): void {
    if (this.isEditMode) {
      this.workScheduleService.getById.reset();
      this.workScheduleService.update.reset();
      this.breadcrumbService.set('@workScheduleEdit', ' ');
    } else {
      this.workScheduleService.add.reset();
      this.workScheduleService.createInstance.reset();
      this.breadcrumbService.set('@workScheduleCreate', ' ');
    }
  }
}
